import React from 'react';
import { Check, Loader2, Send, Trash2, UserCheck, UserPlus } from 'lucide-react';

const StaffTab = ({ ctx }) => {
  const { dashboard, staff, availableClasses, alerts, API_URL, getToken, fetchData, showInviteStaff, invitingStaff, staffForm, setShowInviteStaff, setStaffForm, setInvitingStaff } = ctx;

  return (
<div data-testid="staff-tab">
  <div className="flex items-center justify-between mb-4">
    <div>
      <h1 className="text-xl font-bold" style={{ color: '#1A1209' }}>Team Leads</h1>
      <p className="text-xs" style={{ color: '#999' }}>{staff.length} team lead{staff.length !== 1 ? 's' : ''}</p>
    </div>
    <button onClick={() => setShowInviteStaff(!showInviteStaff)}
      className="px-3 py-2 rounded-lg text-xs font-medium text-white flex items-center gap-1.5"
      style={{ backgroundColor: '#F5831F' }} data-testid="invite-staff-btn">
      <UserPlus size={13} /> Invite Staff
    </button>
  </div>

  {/* Invite form */}
  {showInviteStaff && (
    <div className="bg-white p-4 rounded-xl mb-4" style={{ border: '2px solid #F5831F33' }} data-testid="invite-staff-form">
      <h3 className="text-sm font-bold mb-3" style={{ color: '#1A1209' }}>Invite New Staff Member</h3>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
        <input value={staffForm.name} onChange={e => setStaffForm(p => ({...p, name: e.target.value}))}
          placeholder="Full name *" className="px-3 py-2 border rounded-lg text-sm" style={{ borderColor: '#E0E0E0' }} data-testid="staff-name" />
        <input value={staffForm.email} onChange={e => setStaffForm(p => ({...p, email: e.target.value}))}
          placeholder="Email *" type="email" className="px-3 py-2 border rounded-lg text-sm" style={{ borderColor: '#E0E0E0' }} data-testid="staff-email" />
        <select value={staffForm.role} onChange={e => setStaffForm(p => ({...p, role: e.target.value}))}
          className="px-3 py-2 border rounded-lg text-sm" style={{ borderColor: '#E0E0E0' }} data-testid="staff-role">
          <option value="teacher">Team Lead</option>
          <option value="counselor">Counselor</option>
          <option value="admin">Admin</option>
        </select>
        <select multiple value={staffForm.classes} onChange={e => setStaffForm(p => ({...p, classes: [...e.target.selectedOptions].map(o => o.value)}))}
          className="px-3 py-2 border rounded-lg text-sm" style={{ borderColor: '#E0E0E0' }} data-testid="staff-classes">
          {availableClasses.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      <div className="flex gap-2">
        <button onClick={async () => {
          if (!staffForm.name || !staffForm.email) return;
          setInvitingStaff(true);
          try {
            const res = await fetch(`${API_URL}/api/corporate/staff/invite`, {
              method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
              body: JSON.stringify(staffForm),
            });
            if (res.ok) {
              setStaffForm({ email: '', name: '', role: 'teacher', classes: [] });
              setShowInviteStaff(false);
              fetchData();
            }
          } catch {}
          setInvitingStaff(false);
        }} disabled={invitingStaff || !staffForm.name || !staffForm.email}
          className="px-4 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-50 flex items-center gap-1.5"
          style={{ backgroundColor: '#2EAA5C' }} data-testid="save-staff-btn">
          {invitingStaff ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Send Invite
        </button>
        <button onClick={() => setShowInviteStaff(false)} className="px-4 py-2 rounded-lg text-sm" style={{ backgroundColor: '#F0F0F0', color: '#666' }}>Cancel</button>
      </div>
    </div>
  )}

  {/* Role legend */}
  <div className="flex gap-3 mb-4">
    {[
      { role: 'admin', label: 'Admin', color: '#DC2626', desc: 'Full dashboard access' },
      { role: 'counselor', label: 'Counselor', color: '#8B5CF6', desc: 'Pastoral + alerts only' },
      { role: 'teacher', label: 'Teacher', color: '#2563EB', desc: 'Own class data only' },
    ].map(r => (
      <div key={r.role} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg" style={{ backgroundColor: `${r.color}08` }}>
        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: r.color }} />
        <span className="text-[10px] font-semibold" style={{ color: r.color }}>{r.label}</span>
        <span className="text-[9px]" style={{ color: '#999' }}>— {r.desc}</span>
      </div>
    ))}
  </div>

  {/* Staff list */}
  {staff.length > 0 ? (
    <div className="bg-white rounded-xl overflow-hidden" style={{ border: '1px solid #E8E8E8' }}>
      <table className="w-full">
        <thead>
          <tr style={{ backgroundColor: '#F8F7F4', borderBottom: '1px solid #E8E8E8' }}>
            {['Name', 'Email', 'Role', 'Classes', 'Status', 'Actions'].map(h => (
              <th key={h} className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase" style={{ color: '#999' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {staff.map(s => {
            const roleColors = { admin: '#DC2626', counselor: '#8B5CF6', teacher: '#2563EB' };
            const rc = roleColors[s.role] || '#999';
            return (
              <tr key={s.staff_id} className="hover:bg-gray-50" style={{ borderBottom: '1px solid #F0F0F0' }} data-testid={`staff-row-${s.staff_id}`}>
                <td className="px-4 py-3 text-sm font-medium" style={{ color: '#1A1209' }}>{s.name}</td>
                <td className="px-4 py-3 text-xs" style={{ color: '#666' }}>{s.email}</td>
                <td className="px-4 py-3">
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold capitalize" style={{ backgroundColor: `${rc}15`, color: rc }}>{s.role}</span>
                </td>
                <td className="px-4 py-3 text-xs" style={{ color: '#666' }}>{s.classes?.join(', ') || '—'}</td>
                <td className="px-4 py-3">
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ backgroundColor: s.status === 'active' ? '#ECFDF5' : '#FEF3C7', color: s.status === 'active' ? '#059669' : '#92400E' }}>
                    {s.status === 'active' ? 'Active' : 'Invited'}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <button onClick={async () => {
                    if (!window.confirm(`Remove ${s.name} from staff?`)) return;
                    await fetch(`${API_URL}/api/corporate/staff/${s.staff_id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${getToken()}` } });
                    fetchData();
                  }} className="p-1.5 rounded hover:bg-gray-100" data-testid={`remove-staff-${s.staff_id}`}>
                    <Trash2 size={13} style={{ color: '#DC2626' }} />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  ) : (
    <div className="bg-white rounded-xl p-8 text-center" style={{ border: '1px solid #E8E8E8' }}>
      <UserCheck size={32} style={{ color: '#DDD' }} className="mx-auto mb-3" />
      <p className="text-sm" style={{ color: '#999' }}>No team leads yet. Invite teachers and counselors to give them view-only access to relevant student data.</p>
    </div>
  )}
</div>
  );
};

export default StaffTab;
